/*
  ChapterReader — full-bleed reader with fixed background, scrolling
  text on top, progress bar, transparent-to-frosted topbar, pentimento
  paragraph fade-in, dismissible mid-read email prompt, and a
  read-next section. Ported faithfully from Meta's approved mock.

  Follows the same "stays in DOM, toggle visibility" pattern as
  StoryPicker.js (Rule 74) rather than old Reader.js's append/remove
  pattern - avoids re-litigating stacking-context risk with a second
  approach in the same codebase.

  config: {
    publisher: string (e.g. "STYGIAN MYTH")
    workTitle: string (e.g. "DAWN OF THE FANATIC" or "CROW'S TALE")
    chapterLabel: string (e.g. "PROLOGUE", "CHAPTER I")
    readTime: string (e.g. "8 MIN READ")
    title: string (chapter/story title, e.g. "The Ashen Gate")
    proseHTML: string (pre-built HTML for the article body - caller
      is responsible for turning paragraph data into this string,
      including <p class="lead">, <p class="emphasis">, and the
      <aside id="email-prompt"> insertion point)
    isSubscribed: boolean
    emailPromptThreshold: number 0-1 (default 0.38)
    emailPromptOnceKey: string (localStorage key, should be unique
      per work/chapter so dismissing one doesn't hide prompts on others)
    readNext: array of { title, label, href } (max 3 used)
    backgroundUrl: string
    onClose: function (called when close button/back is triggered)
    onEmailSubmit: function(email) (called on prompt form submit -
      caller wires this to the real Supabase submitEmail.js, NOT
      handled internally here)
    theme: object (optional CSS custom property overrides, e.g.
      { titleSize: '...', fontTitle: '...', accent: '#...' } - keys
      map to the --title-size / --font-title / --accent custom props)
  }
*/

import { createEmailForm, isAlreadySubscribed, showEmailPopover } from './EmailForm.js';

let cachedOverlay = null;
let cachedRefs = null;

function buildOverlay() {
  const overlay = document.createElement('div');
  overlay.dataset.role = 'chapter-reader-overlay';
  overlay.className = 'fixed inset-0 z-[9999]';
  overlay.style.opacity = '0';
  overlay.style.visibility = 'hidden';
  overlay.style.pointerEvents = 'none';
  overlay.style.transition = 'opacity 0.35s ease';
  overlay.style.background = 'var(--paper, #12100E)';
  overlay.style.overflow = 'hidden';

  overlay.innerHTML = `
    <div class="cr-fixed-bg" data-role="cr-fixed-bg" aria-hidden="true"></div>
    <header class="cr-topbar" data-role="cr-topbar">
      <div class="cr-progress-track"><div class="cr-progress-fill" data-role="cr-progress"></div></div>
      <div class="cr-topbar-left">
        <button class="cr-icon-btn" data-role="cr-share" aria-label="Share">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 10.7l6.8-4M8.6 13.3l6.8 4"/></svg>
        </button>
        <button class="cr-icon-btn" data-role="cr-close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>
      <div class="cr-topbar-right"><div class="cr-breadcrumb" data-role="cr-breadcrumb"></div></div>
    </header>
    <div class="cr-scroll-root" data-role="cr-scroll-root">
      <main class="cr-reader">
        <div class="cr-title-wrap">
          <h1 class="cr-title" data-role="cr-title"></h1>
          <div class="cr-kicker" data-role="cr-kicker"></div>
        </div>
        <article class="cr-prose" data-role="cr-prose"></article>
        <section class="cr-read-next" data-role="cr-read-next">
          <div class="cr-rn-label">What to read next</div>
          <div class="cr-rn-grid" data-role="cr-rn-grid"></div>
        </section>
      </main>
    </div>
  `;

  document.getElementById('app-root').appendChild(overlay);

  const refs = {
    overlay,
    fixedBg: overlay.querySelector('[data-role="cr-fixed-bg"]'),
    topbar: overlay.querySelector('[data-role="cr-topbar"]'),
    progress: overlay.querySelector('[data-role="cr-progress"]'),
    breadcrumb: overlay.querySelector('[data-role="cr-breadcrumb"]'),
    title: overlay.querySelector('[data-role="cr-title"]'),
    kicker: overlay.querySelector('[data-role="cr-kicker"]'),
    prose: overlay.querySelector('[data-role="cr-prose"]'),
    scrollRoot: overlay.querySelector('[data-role="cr-scroll-root"]'),
    rnGrid: overlay.querySelector('[data-role="cr-rn-grid"]'),
    shareBtn: overlay.querySelector('[data-role="cr-share"]'),
    closeBtn: overlay.querySelector('[data-role="cr-close"]'),
  };

  refs.closeBtn.addEventListener('click', () => hideReader());

  // Exit fullscreen if the browser's own exit gesture (swipe/back/Esc)
  // was used instead of our close button, so state stays in sync.
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement && overlay.style.visibility === 'visible') {
      // Fullscreen was exited but reader is still open - leave reader
      // open, just no longer fullscreen. Does NOT close the reader.
    }
  });
  refs.shareBtn.addEventListener('click', async () => {
    const data = { title: document.title, url: location.href };
    if (navigator.share) {
      try { await navigator.share(data); } catch (e) {}
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(location.href);
    }
  });

  window.addEventListener('keydown', (e) => {
    if (overlay.style.visibility !== 'visible') return;
    if (e.key === 'Escape') hideReader();
  });

  let scrollObserver = null;
  let onScroll = null;

  async function tryEnterFullscreen() {
    try {
      if (overlay.requestFullscreen) {
        await overlay.requestFullscreen();
      } else if (overlay.webkitRequestFullscreen) {
        await overlay.webkitRequestFullscreen();
      }
      // Silently no-ops on iOS Safari (no Fullscreen API support) - reader
      // still works normally, just without the fullscreen chrome-hiding.
    } catch (e) {
      // User gesture requirements not met, or unsupported - fail silently,
      // reader still opens normally either way.
    }
  }

  function tryExitFullscreen() {
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else if (document.webkitFullscreenElement) {
        document.webkitExitFullscreen?.();
      }
    } catch (e) {}
  }

  function showReader(config) {
    // Apply per-work theme overrides as CSS custom properties.
    // IMPORTANT: overlay is cached/reused across stories (Rule 74), so
    // any custom property set by a PREVIOUS story's config.theme must
    // be explicitly cleared here when the current story has no theme
    // override for that key - otherwise it silently persists onto
    // stories that never asked for it (e.g. Story II's --cr-bg-position
    // leaking into Dawn/Crow's Tale). Track every key we've ever set
    // and reset unset ones to '' (falls back to CSS defaults/var()
    // fallback values) on every call.
    if (!overlay._themedKeys) overlay._themedKeys = new Set();
    overlay._themedKeys.forEach((key) => {
      if (!config.theme || !(key in config.theme)) {
        overlay.style.removeProperty(`--${key}`);
      }
    });
    if (config.theme) {
      Object.entries(config.theme).forEach(([key, val]) => {
        overlay.style.setProperty(`--${key}`, val);
        overlay._themedKeys.add(key);
      });
    }

    refs.fixedBg.style.backgroundImage = config.backgroundUrl ? `url('${config.backgroundUrl}')` : '';
    refs.fixedBg.classList.toggle('cr-bg--radial', !!config.radialVignette);
    if (config.themeClass) {
      overlay.className = overlay.className.replace(/cr-theme--\S+/g, '').trim();
      overlay.classList.add(config.themeClass);
    }
    refs.breadcrumb.innerHTML = `${config.publisher} <span class="cr-dot">\u2022</span> ${config.workTitle}`;
    refs.title.textContent = config.title;
    refs.kicker.innerHTML = `${config.chapterLabel} <span class="cr-dot"></span> ${config.readTime}`;
    refs.prose.innerHTML = config.proseHTML;
    document.title = `${config.title} \u2014 ${config.workTitle}`;

    const readNextItems = (config.readNext || []).slice(0, 4);
    refs.rnGrid.innerHTML = readNextItems.map((item, i) => {
      const clickable = !!(item.href || item.onClick);
      // Use a real <a href> only when there's an actual href to navigate
      // to. For onClick-only items, use a plain div with role="button" -
      // avoids javascript:void(0) href quirks on mobile browsers.
      const tag = item.href ? 'a' : 'div';
      const hrefAttr = item.href ? ` href="${item.href}"` : '';
      const roleAttr = !item.href ? ' role="button" tabindex="0"' : '';
      const inertClass = clickable ? '' : ' cr-rn-item--inert';
      const tapClass = ' cr-rn-item--tappable';
      const trailingHTML = clickable
        ? `<div class="cr-rn-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 6l6 6-6 6"/></svg></div>`
        : `<div class="cr-rn-lock"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="5" y="11" width="14" height="9" rx="1.5"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>Coming Soon</div>`;
      return `<${tag} class="cr-rn-item${inertClass}${tapClass}"${hrefAttr}${roleAttr} data-rn-index="${i}">
        <div><div class="cr-rn-item-title">${item.title}</div>${item.label ? `<div class="cr-rn-item-meta">${item.label}</div>` : ''}</div>
        ${trailingHTML}
      </${tag}>`;
    }).join('');
    refs.rnGrid.querySelectorAll('[data-rn-index]').forEach((el) => {
      const idx = Number(el.dataset.rnIndex);
      const item = readNextItems[idx];
      if (!item) return;

      const handleActivate = (e) => {
        e.preventDefault();
        if (item.onClick) {
          item.onClick();
        } else if (!item.href) {
          // locked / coming-soon item - open the shared popover instead
          showEmailPopover({
            source: item.emailSource || `readnext-${item.title || idx}`,
            message: item.title
              ? `"${item.title}" isn't published yet. Get notified when it drops.`
              : "That's not published yet. Get notified when it drops.",
          });
        }
      };

      if (item.onClick || !item.href) {
        el.addEventListener('click', handleActivate);
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') handleActivate(e);
        });
      }
    });

    // Wire email prompt if present in proseHTML (absent entirely if
    // already subscribed - promptHTML returned '' in that case, so
    // promptEl simply won't exist and none of this runs)
    const promptEl = refs.prose.querySelector('[data-role="cr-email-prompt"]');
    if (promptEl) {
      let promptShown = false;
      let dismissed = false;
      const key = config.emailPromptOnceKey || 'cr_email_dismissed_default';
      try { dismissed = localStorage.getItem(key) === '1'; } catch (e) {}

      const closeBtn = promptEl.querySelector('[data-role="cr-ep-close"]');
      const mount = promptEl.querySelector('[data-role="cr-ep-mount"]');

      const closePrompt = () => {
        promptEl.classList.remove('is-visible');
        promptEl.classList.add('is-dismissed');
        try { localStorage.setItem(key, '1'); } catch (e) {}
      };
      closeBtn?.addEventListener('click', closePrompt);

      if (mount) {
        mount.appendChild(createEmailForm({
          source: config.emailPromptOnceKey || 'chapter-reader-mid-prompt',
          message: '',
        }));
      }

      const threshold = config.emailPromptThreshold ?? 0.38;
      onScroll = () => {
        const el = refs.scrollRoot;
        const scrollTop = el.scrollTop;
        const docH = el.scrollHeight - el.clientHeight;
        const p = docH > 0 ? Math.min(Math.max(scrollTop / docH, 0), 1) : 0;
        refs.progress.style.transform = `scaleX(${p})`;
        refs.topbar.classList.toggle('is-scrolled', p > 0.02);
        if (config.themeClass === 'cr-theme--stayed') {
          overlay.style.setProperty('--cr-scroll-dim', p);
        }
        if (!promptShown && !dismissed && !config.isSubscribed && p > threshold) {
          promptEl.classList.add('is-visible');
          promptShown = true;
        }
      };
    } else {
      onScroll = () => {
        const el = refs.scrollRoot;
        const scrollTop = el.scrollTop;
        const docH = el.scrollHeight - el.clientHeight;
        const p = docH > 0 ? Math.min(Math.max(scrollTop / docH, 0), 1) : 0;
        refs.progress.style.transform = `scaleX(${p})`;
        refs.topbar.classList.toggle('is-scrolled', p > 0.02);
        if (config.themeClass === 'cr-theme--stayed') {
          overlay.style.setProperty('--cr-scroll-dim', p);
        }
      };
    }

    refs.scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Pentimento paragraph fade-in
    if (scrollObserver) scrollObserver.disconnect();
    scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          entry.target.classList.remove('is-past');
        } else if (entry.boundingClientRect.top < 0) {
          entry.target.classList.add('is-past');
          entry.target.classList.remove('is-in');
        }
      });
    }, { root: refs.scrollRoot, rootMargin: '-18% 0px -24% 0px', threshold: 0.15 });
    refs.prose.querySelectorAll('p').forEach((el) => scrollObserver.observe(el));
    scrollObserver.observe(refs.overlay.querySelector('[data-role="cr-read-next"]'));

    overlay.style.visibility = 'visible';
    overlay.style.pointerEvents = 'auto';
    requestAnimationFrame(() => { overlay.style.opacity = '1'; });
    tryEnterFullscreen();

    refs._onClose = config.onClose;

    window.dispatchEvent(new CustomEvent('reader:opened', { detail: { title: config.title } }));
  }

  function hideReader() {
    tryExitFullscreen();
    overlay.style.opacity = '0';
    overlay.style.pointerEvents = 'none';
    if (scrollObserver) scrollObserver.disconnect();
    if (onScroll) refs.scrollRoot.removeEventListener('scroll', onScroll);
    setTimeout(() => { overlay.style.visibility = 'hidden'; }, 350);
    if (refs._onClose) refs._onClose();
  }

  return { overlay, showReader, hideReader, refs };
}

/*
  Shared helper: opens a novel's first chapter/prologue in the reader.
  Single source of truth so main.js's hero CTA and any cross-link
  (e.g. from another reader's read-next section) don't duplicate this
  config-building logic. Both callers should import and use this
  rather than re-building the openChapterReader config by hand.
*/
export async function openNovelPrologue(novelId, readNext = []) {
  const { novelChapters } = await import('../data/novel-chapters.js');
  const { novels } = await import('../data/novels.js');

  const novel = novels.find((n) => n.id === novelId);
  const chapters = novelChapters[novelId];
  if (!novel || !chapters || chapters.length === 0) return;

  const chapter = chapters[0];
  const midIndex = chapter.paragraphs.findIndex(
    (p) => p.type === 'emphasis' && p.text === 'Tired. Happy. Empty.'
  );
  const proseHTML = buildProseFromParagraphs(
    chapter.paragraphs,
    midIndex >= 0 ? midIndex : null,
    {
      headline: "Don't miss what's next.",
      copy: 'Get new myth fragments and early chapters \u2014 once a week, no spam.',
    }
  );

  openChapterReader({
    publisher: 'STYGIAN MYTH',
    workTitle: `${novel.title}${novel.titleAccent ? ' ' + novel.titleAccent : ''}`.toUpperCase(),
    chapterLabel: chapter.chapterLabel,
    readTime: chapter.readTime,
    title: `${chapter.title} \u2014 ${chapter.titleAccent}`,
    proseHTML,
    isSubscribed: false,
    emailPromptOnceKey: `stygian_prompt_${chapter.id}`,
    backgroundUrl: chapter.backgroundUrl,
    radialVignette: true,
    themeClass: 'cr-theme--dawn',
    readNext,
  });
}

export function closeChapterReaderInstant() {
  if (cachedOverlay) {
    const { overlay } = cachedOverlay;
    overlay.style.opacity = '0';
    overlay.style.visibility = 'hidden';
    overlay.style.pointerEvents = 'none';
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else if (document.webkitFullscreenElement) {
        document.webkitExitFullscreen?.();
      }
    } catch (e) {}
  }
}

export function openChapterReader(config) {
  if (!cachedOverlay) {
    cachedOverlay = buildOverlay();
    cachedRefs = cachedOverlay.refs;
  }
  cachedOverlay.showReader(config);
  return cachedOverlay.overlay;
}

export function closeChapterReader() {
  if (cachedOverlay) cachedOverlay.hideReader();
}

/*
  Adapter: converts stories.js's { paragraphs: [{text, style}], midPromptAfter }
  shape into the proseHTML string ChapterReader expects, inserting the
  email-prompt markup at the right index.
*/
export function buildProseFromParagraphs(paragraphs, midPromptAfter, promptCopy) {
  // If already subscribed, render nothing at all - no shell, no confirmation
  // line, per director's instruction that the reader should show zero
  // residual UI once someone's on the list.
  const promptHTML = isAlreadySubscribed()
    ? ''
    : `
    <aside class="cr-email-prompt" data-role="cr-email-prompt" aria-live="polite">
      <div class="cr-ep-top">
        <div class="cr-ep-title">The List</div>
        <button class="cr-ep-close" data-role="cr-ep-close" aria-label="Dismiss"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      </div>
      <h3 class="cr-ep-headline">${promptCopy?.headline || "Don't miss the next chapter."}</h3>
      <p class="cr-ep-copy">${promptCopy?.copy || 'Get new myth fragments and early chapters \u2014 once a week, no spam.'}</p>
      <div class="cr-ep-mount" data-role="cr-ep-mount"></div>
    </aside>`;

  function renderSystemBlock(para) {
    const variantClass = para.variant && para.variant !== 'default' ? ` cr-sys--${para.variant}` : '';
    const linesHTML = para.lines.map((line) => {
      const dimmed = line.startsWith('[') && line.includes(']') && para.lines.indexOf(line) > 0
        ? ' style="opacity:.6"' : '';
      return `<span${dimmed}>${line}</span>`;
    }).join('<br>');
    return `
      <div class="cr-sys${variantClass}" data-char="${para.char || ''}">
        <div class="cr-sys-idx"><span class="cr-sys-dot" aria-hidden="true"></span></div>
        <div class="cr-sys-body">
          <div class="cr-sys-k"><span>${para.kicker || ''}</span><i>${para.meta || ''}</i></div>
          <div class="cr-sys-t">${linesHTML}</div>
        </div>
      </div>`;
  }

  const dividerHTML = `<div class="divider-crack"><svg width="56" height="12" viewBox="0 0 56 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 6 H20 L22 2 L24 10 L26 6 H56" stroke="rgba(168,184,200,0.45)" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/></svg></div>`;

  let html = '';
  let sawLead = false;
  paragraphs.forEach((para, i) => {
    // Auto-insert a divider whenever the narrating voice changes from
    // the previous paragraph (e.g. merchant -> retch -> narration in
    // "The One Who Stayed"). Only fires when paragraphs actually carry
    // a voice field, so stories/chapters without voices (Dawn, Crow's
    // Tale) are completely unaffected. No manual markers needed - this
    // is derived straight from the data.
    if (para.voice && i > 0 && paragraphs[i - 1].voice && para.voice !== paragraphs[i - 1].voice) {
      html += dividerHTML;
    }
    if (para.type === 'system') {
      html += renderSystemBlock(para);
      return;
    }
    let cls = '';
    if (para.type === 'lead' || (!sawLead && para.type !== 'thought' && para.type !== 'onomatopoeia')) {
      cls = 'lead';
      sawLead = true;
    } else if (para.type === 'emphasis') {
      cls = 'emphasis';
    } else if (para.type === 'thought') {
      cls = 'cr-thought';
    } else if (para.type === 'onomatopoeia') {
      cls = 'cr-onomatopoeia';
    } else if (para.type === 'fading-repeat') {
      const wordsHTML = (para.words || [])
        .map((w) => `<span style="font-size:${w.size || '1em'};opacity:${w.opacity != null ? w.opacity : 1};">${w.text}</span>`)
        .join(' ');
      const frVoiceCls = para.voice ? ` cr-voice--${para.voice}` : '';
      html += `<p class="cr-fading-repeat${frVoiceCls}">${wordsHTML}</p>`;
      if (midPromptAfter != null && i === midPromptAfter) {
        html += promptHTML;
      }
      return;
    }
    if (para.voice) {
      cls += ` cr-voice--${para.voice}`;
    }
    const inlineFormattedText = para.text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    html += `<p class="${cls}">${inlineFormattedText}</p>`;
    if (midPromptAfter != null && i === midPromptAfter) {
      html += promptHTML;
    }
  });
  return html;
}
